/**
 * The `/apply` route.
 *
 * ★ THE ROUTER HAS LANDED AND THIS ROUTE IS MOUNTED. This header used to open with *"WHY THERE IS
 * NO ROUTER HERE … `@tanstack/react-router` … is not installed"*, which was true when it was
 * written and is not true now. `src/router.tsx` does exactly the line this file predicted —
 * `createRoute({ path: APPLY_ROUTE.path, component: ApplyRoute })` — and `test/router.spec.ts`
 * asserts that `/apply` reaches the router. The component and the descriptor stay separated, which
 * is what lets the sign-in track pass its two props later without touching the feature — and
 * nothing inside `features/application/` knew or cared either way, which was the point.
 *
 * THE ROUTE PATHS ARE OURS. What is ruled is that the cabinet lives on `authors.moonx.app`, is
 * authenticated, and has exactly two UNAUTHENTICATED routes — `/enter` and `/sign-in`. Everything
 * else is layout and navigation, which decision-log row 99 puts squarely with us.
 *
 * `/terms` IS DELIBERATELY NOT A SIBLING ROUTE HERE. The terms gate is a STEP of this flow, so an
 * applicant never leaves the page they are working on to accept them. The same component is
 * exported from the feature for the OTHER audience — the ~530 grandfathered authors meeting the
 * one-time cutover interstitial — which the cabinet shell owns and mounts at its own route.
 *
 * THIS ROUTE IS AUTHENTICATED BUT NOT CAPABILITY-GATED. `getAuthorSession.canApply` is true for
 * ANY signed-in Firebase user, and `getApplication`, `getCurrentTerms` and `listEditorCategories`
 * all declare 200/401/503 and NO 403. A reader with no author row yet has `cabinetReachable:
 * false` (design 25), so this is one of the few authenticated surfaces they can reach at all.
 */
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import {
  ApplicationApiProvider,
  ApplicationScreen,
  createApplicationApi,
  createQuarantineUploader,
  readFixtureScenario,
} from '../../features/application';
import { AUTHORS_API_BASE_URL } from '../../config';

/** A route descriptor, so the router wiring has one thing to read when it arrives. */
export const APPLY_ROUTE = {
  path: '/apply',
  requiresAuth: true,
  /** No capability is needed: any signed-in user may apply. */
  requiresAuthorCapability: false,
} as const;

export interface ApplyRouteProps {
  /**
   * Returns a CURRENT Firebase ID token. Omitted - and the router omits it - the application seam
   * falls back to the app's one token source, the auth client's current session; a test passes its
   * own. Optional rather than faked with a placeholder string.
   */
  readonly getIdToken?: (() => Promise<string | undefined>) | undefined;
  /** The way into the cabinet. The router gives it only where the page is drawn outside it. */
  readonly onOpenCabinet?: (() => void) | undefined;
}

export function ApplyRoute({ getIdToken, onOpenCabinet }: ApplyRouteProps): ReactNode {
  // Created ONCE per mount. The fixture holds state between calls, so re-creating it on every
  // render would reset the application every time anything on the page changed.
  const api = useMemo(() => {
    const scenario = readFixtureScenario(window.location.search);
    return createApplicationApi({
      baseUrl: AUTHORS_API_BASE_URL,
      ...(getIdToken === undefined ? {} : { getIdToken }),
      ...(scenario === undefined ? {} : { scenario }),
    });
  }, [getIdToken]);

  const uploader = useMemo(() => createQuarantineUploader(), []);

  return (
    <ApplicationApiProvider api={api} uploader={uploader}>
      <ApplicationScreen onOpenCabinet={onOpenCabinet} />
    </ApplicationApiProvider>
  );
}
